<!-- Dernières analyses (tableau / cartes). Remplace l’ancien GraphAdmin.vue. -->
<template>
  <div class="flex w-full flex-col">
    <div v-if="loading" class="flex w-full justify-center py-10">
      <div
        class="h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"
        role="status"
      />
      <span class="sr-only">Chargement...</span>
    </div>

    <div v-if="error" class="flex w-full items-start p-4">
      <div class="w-full rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
        {{ error }}
      </div>
    </div>

    <div v-if="!loading && !error" class="flex w-full flex-col overflow-hidden py-5">
      <div
        class="flex flex-col overflow-hidden rounded-xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_rgba(0,0,0,0.08)]"
      >
        <h5 class="mb-4 text-lg font-semibold text-slate-900">Dernières analyses</h5>

        <div v-if="isMobile" class="flex flex-col gap-4">
          <div
            v-for="a in analysesList"
            :key="a.id"
            class="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm"
          >
            <div class="mb-3 flex items-start justify-between gap-3">
              <strong class="text-base font-semibold text-slate-900">{{ a.client_name || '—' }}</strong>
              <span
                :class="getStatusBadgeClass(a.recommandation_ia_statut)"
                :title="getStatusTooltip(a)"
              >
                <component
                  :is="getStatusIconComponent(a.recommandation_ia_statut)"
                  class="h-4 w-4 shrink-0"
                  aria-hidden="true"
                />
                {{ a.recommandation_ia_statut_display || '—' }}
              </span>
            </div>
            <div class="mb-2 flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-3">
              <span class="text-sm font-semibold text-slate-600">Email</span>
              <span class="text-right text-sm text-slate-700 break-words sm:text-left">{{ a.client_email || '—' }}</span>
            </div>
            <div class="mb-2 flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-3">
              <span class="text-sm font-semibold text-slate-600">Création</span>
              <span class="text-right text-sm text-slate-600 sm:text-left">{{ formatDate(a.date_creation) }}</span>
            </div>
            <div class="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-3">
              <span class="text-sm font-semibold text-slate-600">Dernière modif.</span>
              <span class="text-right text-sm text-slate-600 sm:text-left">{{ formatDate(a.date_modification) }}</span>
            </div>
          </div>
          <div v-if="analysesList.length === 0" class="py-4 text-center text-sm text-slate-500">
            Aucune analyse trouvée.
          </div>
        </div>

        <div v-else class="max-h-[500px] overflow-auto">
          <table class="mb-0 min-w-full border-collapse text-sm staff-table">
            <thead class="sticky top-0 z-10">
              <tr>
                <th>Client</th>
                <th>Email</th>
                <th>Date de création</th>
                <th>Dernière modif.</th>
                <th class="text-center">Recommandations</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="a in analysesList"
                :key="a.id"
                class="border-b border-slate-100 transition hover:bg-slate-50/80"
              >
                <td class="max-w-[150px] truncate px-3 py-3 align-middle font-medium text-slate-900">
                  {{ a.client_name }}
                </td>
                <td class="max-w-[180px] truncate px-3 py-3 align-middle text-slate-600">{{ a.client_email }}</td>
                <td class="whitespace-nowrap px-3 py-3 align-middle text-slate-600">{{ formatDate(a.date_creation) }}</td>
                <td class="whitespace-nowrap px-3 py-3 align-middle text-slate-600">{{ formatDate(a.date_modification) }}</td>
                <td class="px-3 py-3 text-center align-middle">
                  <div class="inline-block">
                    <span
                      :class="getStatusBadgeClass(a.recommandation_ia_statut)"
                      :title="getStatusTooltip(a)"
                    >
                      <component
                        :is="getStatusIconComponent(a.recommandation_ia_statut)"
                        class="h-4 w-4 shrink-0"
                        aria-hidden="true"
                      />
                      {{ a.recommandation_ia_statut_display || '—' }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-if="analysesList.length === 0">
                <td colspan="5" class="py-4 text-center text-slate-500">Aucune analyse trouvée.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  EllipsisHorizontalCircleIcon,
} from '@heroicons/vue/24/outline';
import { isAuthenticatedSession } from '@/utils/sessionAuth';

const TOKEN_KEYS = { ACCESS: 'auth_session' };

export default {
  name: 'AdminAnalysesList',
  components: {
    DocumentTextIcon,
    ClockIcon,
    CheckCircleIcon,
    EllipsisHorizontalCircleIcon,
  },
  data() {
    return {
      analysesList: [],
      isMobile: false,
      loading: false,
      error: null,
    };
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    this.fetchAnalyses();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    },
    /**
     * L'API patrimoine renvoie client_id + client_snapshot (nom/prenom/username/email),
     * pas les champs plats client_name / client_email attendus par ce template.
     */
    normalizeAnalysisItem(item) {
      if (!item || typeof item !== 'object') return item;
      const snap = item.client_snapshot && typeof item.client_snapshot === 'object' ? item.client_snapshot : {};
      const nom = String(snap.nom || '').trim();
      const prenom = String(snap.prenom || '').trim();
      const fullName = `${prenom} ${nom}`.trim();
      return {
        ...item,
        client_name: item.client_name || fullName,
        client_email: item.client_email || snap.email || '',
      };
    },
    async fetchAnalyses() {
      this.loading = true;
      this.error = null;

      try {
        if (!isAuthenticatedSession()) {
          this.error = "Token d'authentification manquant";
          this.loading = false;
          return;
        }
        const response = await axios.get('/api/toutes-analyses/', { withCredentials: true });
        const raw = response.data;
        const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.analyses) ? raw.analyses : []);
        this.analysesList = list.map((item) => this.normalizeAnalysisItem(item));
        this.analysesList.sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation));
      } catch (err) {
        console.error('Erreur lors de la récupération des analyses:', err);
        this.error = 'Impossible de charger les analyses. Veuillez réessayer plus tard.';
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      return new Date(dateString).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    getStatusBadgeClass(status) {
      const base =
        'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold cursor-help transition';
      switch (status) {
        case 'brouillon':
          return `${base} border-slate-200 bg-slate-100 text-slate-700`;
        case 'en_attente':
          return `${base} border-amber-200 bg-amber-50 text-amber-900`;
        case 'publiee':
          return `${base} border-emerald-200 bg-emerald-50 text-emerald-900`;
        default:
          return `${base} border-slate-200 bg-slate-50 text-slate-600`;
      }
    },
    getStatusIconComponent(status) {
      const map = {
        brouillon: DocumentTextIcon,
        en_attente: ClockIcon,
        publiee: CheckCircleIcon,
      };
      return map[status] || EllipsisHorizontalCircleIcon;
    },
    getStatusTooltip(analyse) {
      const status = analyse.recommandation_ia_statut;
      const tooltips = {
        brouillon: 'Non finalisée - À revérifier avant publication',
        en_attente: 'En attente de validation - À examiner',
        publiee: 'Recommandation validée et publiée',
      };
      return tooltips[status] || 'Statut inconnu';
    },
  },
};
</script>
